import { FilledDynamicValueTableColumnType } from "../DynamicValue.js";
import { zGuard } from "../zod/utils.js";
import { assertNever } from "../errors.js";
import { ColumnOperation, Filter, FilterGroup } from "../filter/index.js";
import { FilterCellColumnId } from "../idTypeBrands.js";

import {
  BinaryColumnPredicate,
  ColumnPredicate,
  CompoundColumnPredicate,
  ListBinaryColumnPredicate,
  LumpColumnPredicate,
  UnaryColumnPredicate,
} from "./columnPredicateTypes.js";
import { ColumnFilter } from "./filterTypes.js";
import { DisplayTableColumnOutputType } from "./outputTypes.js";

export function displayTableFilterToFilterCellFilter(
  filter: ColumnFilter,
  columnTypes?: Record<string, DisplayTableColumnOutputType>,
): Filter | FilterGroup {
  const { column, predicate } = filter;
  let operation: ColumnOperation;
  if (
    zGuard(UnaryColumnPredicate, predicate) ||
    zGuard(BinaryColumnPredicate, predicate) ||
    zGuard(ListBinaryColumnPredicate, predicate) ||
    zGuard(LumpColumnPredicate, predicate)
  ) {
    operation = predicate;
  } else if (zGuard(CompoundColumnPredicate, predicate)) {
    return {
      operation: predicate.op,
      filters: predicate.args.map((pred) =>
        displayTableFilterToFilterCellFilter({
          column,
          predicate: pred,
          columnType: columnTypes?.[column] ?? filter.columnType,
        }),
      ),
    };
  } else {
    assertNever(predicate, predicate);
  }

  return {
    column: FilterCellColumnId.parse(column),
    operation,
    columnType:
      columnTypes?.[column] ??
      filter.columnType ??
      FilledDynamicValueTableColumnType.UNKNOWN,
  };
}

export function columnFilterFullyConfigured(filter: ColumnFilter): boolean {
  if (filter.column == null || filter.column.trim().length === 0) {
    return false;
  }

  function predicateConfigured(predicate: ColumnPredicate): boolean {
    if (zGuard(UnaryColumnPredicate, predicate)) {
      return true;
    } else if (
      zGuard(BinaryColumnPredicate, predicate) ||
      zGuard(LumpColumnPredicate, predicate)
    ) {
      return predicate.arg != null && predicate.arg.trim().length > 0;
    } else if (zGuard(ListBinaryColumnPredicate, predicate)) {
      return predicate.arg != null && predicate.arg.length > 0;
    } else if (zGuard(CompoundColumnPredicate, predicate)) {
      return predicate.args.every(predicateConfigured);
    } else {
      assertNever(predicate, predicate);
    }
  }

  return predicateConfigured(filter.predicate);
}

export function filterKey({
  column,
  columnType,
  predicate,
}: ColumnFilter): string {
  return `${column}//${columnType ?? ""}//${filterPredicateKey(predicate)}`;
}

function filterPredicateKey(predicate: ColumnPredicate): string {
  if (zGuard(UnaryColumnPredicate, predicate)) {
    return `${predicate.op}`;
  } else if (
    zGuard(BinaryColumnPredicate, predicate) ||
    zGuard(ListBinaryColumnPredicate, predicate) ||
    zGuard(LumpColumnPredicate, predicate)
  ) {
    return `${predicate.op}//${predicate.arg}`;
  } else if (zGuard(CompoundColumnPredicate, predicate)) {
    return `${predicate.op}//${predicate.args
      .map(filterPredicateKey)
      .join("//")}`;
  }
  assertNever(predicate, predicate);
}
